/**
 * Active Profile Store
 *
 * Tracks which team profile is currently active on THIS device. Persisted to
 * the local storage adapter (AsyncStorage on mobile, localStorage on web) so
 * the same person doesn't have to re-pick themselves every cold launch and so
 * full-page navigations / URL-bar refreshes don't bounce them to the picker.
 *
 * Cleared on Firebase Auth signout. Cleared also on "Switch Profile" to return
 * the user to the picker without affecting auth.
 *
 * NOT in Firestore — this is per-device app state.
 *
 * Platform note: on web we bypass the shared `getStorageAdapter()` indirection
 * and bind directly to `localStorage`. The shared adapter is set in
 * `providers.tsx`, but ES module import order means this store can evaluate
 * before `setStorageAdapter()` runs, in which case Zustand's persist falls
 * back to an in-memory map and the user gets bounced to the picker on every
 * refresh. Pinning to `localStorage` directly (when `window` exists) closes
 * that race.
 */

import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";
import { getStorageAdapter } from "../config/storageAdapter";

interface ActiveProfileState {
  activeProfileId: string | null;
  /**
   * Which vendor the active profile belongs to. The active profile is only
   * valid while THIS is the vendor currently subscribed in teamProfilesStore.
   * Without this scoping, a profile picked in venue A could leak into venue B
   * after an enterVenue() auth swap, or a previous account's profile could
   * survive a sign-out → sign-in on a different account (both were real bugs).
   * null for legacy persisted values (pre-scoping) — the membership check in
   * useActiveProfile still gates those by profile-id presence.
   */
  vendorId: string | null;
  /** True once Zustand has finished pulling the persisted value from storage. */
  _hasHydrated: boolean;
  /**
   * Set after a profile is picked (or auto-resumed) on this device. The
   * vendorId scopes the pick to the account it was made under.
   */
  setActiveProfile: (profileId: string, vendorId: string) => void;
  /** Clear on signout or "Switch Profile". */
  clearActiveProfile: () => void;
}

// Pick the right storage backend without relying on `setStorageAdapter()` being
// called before this module evaluates. On web, bind directly to `localStorage`.
// On native (and during SSR), fall back to the shared adapter set in App.tsx.
function pickStorage(): StateStorage {
  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    return {
      getItem: (key: string) => window.localStorage.getItem(key),
      setItem: (key: string, value: string) => {
        window.localStorage.setItem(key, value);
      },
      removeItem: (key: string) => {
        window.localStorage.removeItem(key);
      },
    };
  }
  return getStorageAdapter();
}

export const useActiveProfileStore = create<ActiveProfileState>()(
  persist(
    (set) => ({
      activeProfileId: null,
      vendorId: null,
      _hasHydrated: false,
      setActiveProfile: (profileId, vendorId) =>
        set({ activeProfileId: profileId, vendorId }),
      clearActiveProfile: () => set({ activeProfileId: null, vendorId: null }),
    }),
    {
      name: "eventify-active-profile",
      version: 1,
      storage: createJSONStorage(() => pickStorage()),
      // Persist the id + the vendor it was picked under — never the action
      // functions or the hydration flag (the flag is reset via onRehydrateStorage).
      partialize: (state) => ({
        activeProfileId: state.activeProfileId,
        vendorId: state.vendorId,
      }),
      // v0 → v1: legacy payloads have only { activeProfileId }. Stamp vendorId:
      // null so the useActiveProfile membership check governs them (it can't
      // vendor-scope a legacy pick, but the profile-id presence check still
      // prevents a foreign id from resolving).
      migrate: (persisted, version) => {
        if (version === 0 && persisted && typeof persisted === "object") {
          return { ...(persisted as Record<string, unknown>), vendorId: null } as ActiveProfileState;
        }
        return persisted as ActiveProfileState;
      },
      onRehydrateStorage: () => (state) => {
        if (state) state._hasHydrated = true;
      },
    }
  )
);
